import React from 'react';

import Icon from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import SelectBox from '../SelectBox';

export const CREATE_TYPE = '__selectbox_create_option__';

export function defaultMenuRenderer({ items, renderItem }) {
  return items.map((item, index) => renderItem({ item, index }));
}

export function defaultValueRenderer(value, { disabled, stateAndHelpers }) {
  const { itemToString } = stateAndHelpers;
  return (
    <div
      css={[
        theme.typography.labelMedium,
        {
          color: disabled ? theme.colors.charcoal500 : theme.colors.charcoal900,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      ]}
    >
      {itemToString(value)}
    </div>
  );
}

export function defaultValueTagRenderer(item, selectProps) {
  const {
    disabled,
    valueKey,
    labelKey,
    subtextKey,
    container,
    stateAndHelpers,
  } = selectProps;
  const { isOpen, closeMenu, getItemProps, getValueTagProps } = stateAndHelpers;
  const content = item[labelKey] || item[subtextKey];

  return (
    <button
      key={item[valueKey]}
      data-testid="SelectBox__tag"
      css={[
        theme.typography.bodyMedium,
        {
          display: 'flex',
          flexDirection: 'row',
          maxWidth: 200,
          height: 27,
          padding: '0 6px',
          margin: '3px 6px 3px 0',
          color: theme.colors.charcoal900,
          backgroundColor: theme.colors.charcoal100,
          borderRadius: 4,
          border: 'none',
          alignItems: 'center',
          cursor: 'default',
        },
      ]}
      {...getValueTagProps}
    >
      <OverflowTooltip
        singleLine
        autoTipOnEllipsis
        container={container}
        getTargetProps={() => ({ style: { maxWidth: 172 } })}
      >
        {content}
      </OverflowTooltip>

      {!disabled && (
        <Icon
          size={12}
          type={Icon.TYPES.X}
          style={{ marginLeft: 4 }}
          onClick={(e) => {
            e.stopPropagation();
            getItemProps({ item }).onClick(e);
            if (!isOpen) closeMenu();
          }}
        />
      )}
    </button>
  );
}

export function defaultLabelRenderer(label, subtext, disabled) {
  return (
    <div
      css={[
        theme.typography.ellipsis,
        {
          color: disabled ? theme.colors.charcoal500 : theme.colors.charcoal900,
        },
      ]}
      title={typeof label === 'string' ? label : undefined}
    >
      {label}
      {!!subtext && (
        <div
          css={[
            theme.typography.ellipsis,
            theme.typography.bodyMedium,
            {
              ...(!disabled && { color: theme.colors.charcoal700 }),
            },
          ]}
          title={typeof subtext === 'string' ? subtext : undefined}
        >
          {subtext}
        </div>
      )}
    </div>
  );
}

export function defaultOptionRenderer(option, selected, selectProps, disabled) {
  const { labelKey, labelGetter, subtextKey, subtextGetter } = selectProps;
  const label = labelGetter ? labelGetter(option) : option[labelKey];
  const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];

  return defaultLabelRenderer(label, subtext, disabled);
}

export function defaultCreateOptionRenderer(
  option,
  selected,
  selectProps,
  disabled
) {
  const { labelKey, labelGetter, subtextKey, subtextGetter } = selectProps;

  let label = labelGetter ? labelGetter(option) : option[labelKey];
  if (!selected) label = selectProps['T'].translate('CREATE_OPTION', { label });
  const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];

  return defaultLabelRenderer(label, subtext, disabled);
}

export function defaultItemRenderer({
  item,
  index,
  selected,
  disabled,
  itemProps,
  selectProps,
}) {
  const {
    multiple,
    optionRenderer = defaultOptionRenderer,
    createOptionRenderer = defaultCreateOptionRenderer,
    groupLabelKey,
  } = selectProps;

  const { highlighted } = itemProps;

  const isGroupedSelectBox = !!groupLabelKey;

  if (item[CREATE_TYPE] && !selected) {
    return (
      <li {...itemProps}>
        {createOptionRenderer(item, selected, selectProps, disabled)}
      </li>
    );
  }

  return (
    <li
      data-testid={`SelectBox__option--${index}`}
      css={[
        {
          cursor: 'pointer',
          display: 'flex',
          padding: `${index === 0 && !isGroupedSelectBox ? 16 : 8}px 16px 8px`,
          backgroundColor: theme.colors.white,

          ...(!disabled &&
            (!selected || multiple) && {
              '&:hover': {
                backgroundColor: theme.colors.charcoal050,
              },

              '&:active': {
                backgroundColor: theme.colors.charcoal100,
              },
            }),

          ...(disabled && {
            cursor: 'default',
          }),

          ...(!disabled &&
            highlighted && {
              backgroundColor: theme.colors.charcoal050,
            }),

          ...(selected &&
            !multiple && {
              backgroundColor: theme.colors._adskBlue100Alpha,

              ...theme.typography.bodyMediumBold,
            }),
        },
      ]}
      {...itemProps}
    >
      {multiple && (
        <div
          css={[
            {
              alignItems: 'center',
              borderRadius: 2,
              display: 'flex',
              flexShrink: 0,
              height: 16,
              marginRight: 8,
              marginTop: 2,
              width: 16,

              ...(!selected && {
                border: `1px solid ${theme.colors.charcoal400}`,
              }),

              ...(selected && {
                backgroundColor: theme.colors.adskBlue500,
                color: theme.colors.white,
                fontSize: 10,
              }),
            },
          ]}
        >
          {selected && <Icon type={Icon.TYPES.CHECKMARK} size={16} />}
        </div>
      )}
      {optionRenderer(item, selected, selectProps, disabled)}
      {!multiple && <span css={[{ flex: 1 }]} />}
      {!multiple && selected && (
        <Icon
          style={{
            color: theme.colors.adskBlue500,
            flexShrink: 0,
            marginLeft: 8,
          }}
          size={24}
          type={Icon.TYPES.CHECKMARK}
        />
      )}
    </li>
  );
}

export function defaultFilterOptions(options, inputValue, selectProps) {
  const search = inputValue.trim().toLowerCase();
  if (!search) return options;

  const { labelKey, labelGetter, subtextKey, subtextGetter } = selectProps;
  return options.filter((option) => {
    const label = labelGetter ? labelGetter(option) : option[labelKey];
    const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];
    return (
      (label && label.toLowerCase().indexOf(search) >= 0) ||
      (subtext && subtext.toLowerCase().indexOf(search) >= 0)
    );
  });
}

export function defaultSelectBoxRenderer(props) {
  return <SelectBox {...props} />;
}

export function defaultGroupHeaderRenderer(group, headerProps, selectProps) {
  const { groupLabelKey, groupLabelGetter } = selectProps;
  const groupLabel = groupLabelGetter
    ? groupLabelGetter(group)
    : group[groupLabelKey];

  return (
    <li
      {...headerProps}
      css={[
        theme.typography.bodySmall,
        {
          backgroundColor: theme.colors.charcoal050,
          padding: '8px 16px',
        },
      ]}
    >
      {groupLabel}
    </li>
  );
}
